import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { PROFILE } from '../../router/routes';
import { RootState } from '../../store/store';
import User from '../../interfaces/user';
import { useService } from '../../services/services';
import Form, { SubmitType } from '../../components/form';
import Disabled from '../../components/form/elements/disabled';
import Text from '../../components/form/elements/text';
import Group from '../../components/form/elements/group';
import HtmlBlock from '../../components/form/elements/html-block';
import Button from '../../components/form/elements/button';
import Link from '../../components/form/elements/link';
import Password from '../../components/form/elements/password';
import css from './index.module.css';
import debounce from 'awesome-debounce-promise';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';

export interface UserData extends User {
  password: string;
  new_password: string;
  new_password_confirmation: string;
}

export default function ChangePasswordForm() {
  const { profileService, authService } = useService();
  const history = useHistory();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => ({
    user: state.global.user,
  }));

  const [passwordFormSchema] = useState(() =>
    yup.object().shape({
      password: yup.lazy(() =>
        user.hasPassword
          ? yup.string().max(255).required()
          : yup.string().max(255).notRequired()
      ),
      new_password: yup
        .string()
        .max(255)
        .required()
        .test(
          'passwordValid',
          'Password does not follow the rules',
          debounce(
            (value) =>
              authService
                .validatePassword(value)
                .pipe(
                  catchError(() => of(false)),
                  map((value) => value !== false)
                )
                .toPromise(),
            1500
          )
        ),
      new_password_confirmation: yup
        .string()
        .max(255)
        .required()
        .oneOf(
          [yup.ref('new_password')],
          'pages.profile.messages.new_password_confirmation_error'
        ),
    })
  );
  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements] = useState(() => [
    Button('save', null, 'change'),
    Link(PROFILE, 'cancel'),
  ]);

  useEffect(() => {
    setBodyElements([
      Group([
        Disabled([Text('name'), Text('email')]),
        ...(user.hasPassword
          ? [Password('password', 'pages.profile.fields.password')]
          : []),
        Password('new_password', 'pages.profile.fields.new_password'),
        HtmlBlock(
          null,
          <span className={css['password-info-message']}>
            {t('pages.profile.messages.password')}
            <ul className={css['password-criteria']}>
              <li>{t('pages.profile.messages.letter')}</li>
              <li>{t('pages.profile.messages.special')}</li>
              <li>{t('pages.profile.messages.number')}</li>
            </ul>
          </span>
        ),
        Password(
          'new_password_confirmation',
          'pages.profile.fields.new_password_confirmation'
        ),
      ]),
    ]);
  }, [user, t, history]);

  const submit = useCallback<SubmitType<UserData, void>>(
    (form) => [
      profileService.changePassword(
        form.password,
        form.new_password,
        form.new_password_confirmation
      ),
      () => {
        history.push(PROFILE);
      },
    ],
    [profileService, history]
  );

  return (
    <Form
      mode={'create'}
      title={t('pages.profile.title')}
      backTo={null}
      body={bodyElements}
      footer={footerElements}
      values={user}
      submit={submit}
      schema={passwordFormSchema}
    />
  );
}
